import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import '../components/CommonButton.css'; // Importa stili comuni per i pulsanti
import { saveBracket, loadBracket, loadTeams, loadMatches } from '../utils/storage';
import { generateAssaltiFederali } from '../utils/fencingLogic';
import RotateIcon from '../components/RotateIcon';
import { TrophyIcon } from '../components/FencingIcons';


// Funzione di utilità per creare le posizioni standard nel tabellone FIE
const createStandardFIEBracketPositions = (numPositions, numAthletes) => {
  // Implementazione esatta del tabellone secondo lo schema standard FIE (come nell'immagine)

  // Calcola la dimensione del tabellone (potenza di 2 più vicina che contiene tutti gli atleti)
  const bracketSize = numPositions * 2;

  const positions = Array(bracketSize).fill(-1);

  // FIX: Gestione specifica per tabellone a 2 (Finale diretta)
  if (bracketSize === 2) {
    positions[0] = 0; // Testa di serie 1
    if (numAthletes > 1) positions[1] = 1; // Testa di serie 2
    return positions;
  }

  // === TABELLONE STANDARD A 32 POSIZIONI ===
  // Schema delle posizioni nel tabellone come mostrato nell'immagine di riferimento
  // Ogni coppia rappresenta [posizione nel tabellone, posizione in classifica]
  const standardBracket32 = [
    // Prima metà del tabellone
    [0, 0],   // 1 vs 32
    [1, 31],
    [2, 16],  // 17 vs 16
    [3, 15],
    [4, 8],   // 9 vs 24
    [5, 23],
    [6, 24],  // 25 vs 8
    [7, 7],
    [8, 4],   // 5 vs 28
    [9, 27],
    [10, 20], // 21 vs 12
    [11, 11],
    [12, 12], // 13 vs 20
    [13, 19],
    [14, 28], // 29 vs 4
    [15, 3],
    // Seconda metà del tabellone
    [16, 2],  // 3 vs 30
    [17, 29],
    [18, 18], // 19 vs 14
    [19, 13],
    [20, 10], // 11 vs 22
    [21, 21],
    [22, 26], // 27 vs 6
    [23, 5],
    [24, 6],  // 7 vs 26
    [25, 25],
    [26, 22], // 23 vs 10
    [27, 9],
    [28, 14], // 15 vs 18
    [29, 17],
    [30, 30], // 31 vs 2
    [31, 1],
  ];

  // Schema per tabellone a 16 posizioni (adattato da quello a 32)
  const standardBracket16 = [
    [0, 0],   // 1 vs 16
    [1, 15],
    [2, 8],   // 9 vs 8
    [3, 7],
    [4, 4],   // 5 vs 12
    [5, 11],
    [6, 12],  // 13 vs 4
    [7, 3],
    [8, 2],   // 3 vs 14
    [9, 13],
    [10, 10], // 11 vs 6
    [11, 5],
    [12, 6],  // 7 vs 10
    [13, 9],
    [14, 14], // 15 vs 2
    [15, 1],
  ];

  // Schema per tabellone a 8 posizioni
  const standardBracket8 = [
    [0, 0],  // 1 vs 8
    [1, 7],
    [2, 4],  // 5 vs 4
    [3, 3],
    [4, 2],  // 3 vs 6
    [5, 5],
    [6, 6],  // 7 vs 2
    [7, 1],
  ];

  // Scegli lo schema appropriato in base alla dimensione del bracket
  let standardBracket;
  if (bracketSize === 32) {
    standardBracket = standardBracket32;
  } else if (bracketSize === 16) {
    standardBracket = standardBracket16;
  } else if (bracketSize === 8) {
    standardBracket = standardBracket8;
  } else {
    // Per altri casi (es. 4), usa uno schema basico
    standardBracket = [
      [0, 0], [1, 3], [2, 2], [3, 1]
    ];
  }

  // Riempie il tabellone in base allo schema standard
  for (const [pos, seedIdx] of standardBracket) {
    // Se abbiamo ancora atleti disponibili, assegna l'atleta alla posizione
    if (seedIdx < numAthletes) {
      positions[pos] = seedIdx;
    }
    // Altrimenti rimane -1 (BYE)
  }

  return positions;
};

const EliminationBracket = ({ route }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Stato per tenere traccia dell'accesso consentito
  const [accessDenied, setAccessDenied] = useState(false);

  // Verifica se tutti gli assalti del girone sono completati
  useEffect(() => {
    const matches = loadMatches() || [];
    const allMatchesCompleted = matches.length > 0 && matches.every(match => match.completed === true);

    // Se ci sono assalti non completati e non è stato esplicitamente bypassato, blocca l'accesso
    if (!allMatchesCompleted && !location.state?.bypassCheck) {
      setAccessDenied(true);
    }
  }, [location.state]);

  // Funzione per ritornare alla pagina degli assalti
  const handleReturnToMatches = useCallback(() => {
    navigate("/schedule");
  }, [navigate]);

  // Recupera teams e standings solo una volta all'avvio
  const [localTeams, setLocalTeams] = useState([]);
  const [localStandings, setLocalStandings] = useState([]);
  const [brackets, setBrackets] = useState([]);
  const [incomingBracket, setIncomingBracket] = useState(null);
  const [dataCorrupted, setDataCorrupted] = useState(false);
  // Modalità di visualizzazione del tabellone
  const [viewMode, setViewMode] = useState('pyramid'); // 'pyramid' o 'linear'
  const [zoomLevel, setZoomLevel] = useState(1); // Livello di zoom (1 = normale)
  // --- Stato per modale di conferma reset ---
  const [showResetModal, setShowResetModal] = useState(false);

  // Utility per nome squadra leggibile
  const getTeamDisplayName = useCallback((team) => {
    if (!team) return "TBD";
    if (team.name === "BYE" || team.name === "TBD") return team.name;
    if (team.displayName) return team.displayName;
    if (team.firstName && team.lastName) return `${team.lastName} ${team.firstName.charAt(0)}.`;
    // Se è un team o un giocatore singolo
    return team.name || "TBD";
  }, []);

  // Utility per ottenere la posizione nel girone
  const getTeamSeedingPosition = useCallback((team) => {
    if (!team || team.name === "BYE" || team.name === "TBD") return null;

    // Prima cerchiamo per ID (se presente)
    if (team.id !== undefined) {
      const positionById = localStandings.findIndex(s => s && s.id === team.id);
      if (positionById >= 0) return positionById + 1;
    }

    // Se non trovato per ID, proviamo per nome (backup)
    if (team.name) {
      const positionByName = localStandings.findIndex(s =>
        s && ((s.name && s.name === team.name) ||
          (s.lastName && team.lastName && s.lastName === team.lastName)));
      if (positionByName >= 0) return positionByName + 1;
    }

    // Se ancora non trovato, restituiamo null
    return null;
  }, [localStandings]);

  // Patch bracket: aggiorna sempre i nomi delle squadre anche su bracket già salvati
  const patchBracketTeamNames = useCallback((bracket, teams) => {
    if (!Array.isArray(bracket) || bracket === null) return bracket;
    return bracket.map((round, roundIdx) =>
      Array.isArray(round)
        ? round.map((match, matchIdx) => {
          const t1 = match.team1 && typeof match.team1 === 'object' && typeof match.team1.id === 'number'
            ? teams[match.team1.id] || match.team1
            : match.team1;
          const t2 = match.team2 && typeof match.team2 === 'object' && typeof match.team2.id === 'number'
            ? teams[match.team2.id] || match.team2
            : match.team2;
          const patchedMatch = {
            ...match,
            team1: { ...t1, name: getTeamDisplayName(t1) },
            team2: { ...t2, name: getTeamDisplayName(t2) }
          };
          return patchedMatch;
        })
        : round
    );
  }, [getTeamDisplayName]);

  // Funzione per far avanzare automaticamente i vincitori contro un BYE
  const advanceByeWinners = useCallback((inputBracket) => {
    // Se non viene passato un bracket, usiamo quello nello stato (se disponibile)
    const bracketToProcess = inputBracket || brackets;

    if (!Array.isArray(bracketToProcess) || bracketToProcess.length === 0) {
      return bracketToProcess;
    }

    // Clone del bracket per evitare mutazioni dirette
    let newBracket = JSON.parse(JSON.stringify(bracketToProcess));
    let changed = false;

    // Iteriamo attraverso i round (tranne l'ultimo che è la finale)
    for (let r = 0; r < newBracket.length - 1; r++) {
      for (let m = 0; m < newBracket[r].length; m++) {
        const match = newBracket[r][m];

        // Se il match è già completato, non facciamo nulla
        if (match.completed) continue;

        let winner = null;
        let winnerNum = 0;

        // Se team1 è BYE e team2 è una squadra valida, vince team2
        if (match.team1?.name === 'BYE' && match.team2 && match.team2.name !== 'BYE' && match.team2.name !== 'TBD') {
          winner = match.team2;
          winnerNum = 2;
        }
        // Se team2 è BYE e team1 è una squadra valida, vince team1
        else if (match.team2?.name === 'BYE' && match.team1 && match.team1.name !== 'BYE' && match.team1.name !== 'TBD') {
          winner = match.team1;
          winnerNum = 1;
        }

        if (winner) {
          match.completed = true;
          match.winner = winnerNum;
          match.score1 = 0;
          match.score2 = 0;

          const nextRoundIdx = r + 1;
          const nextMatchIdx = Math.floor(m / 2);
          const isTeam1 = (m % 2 === 0);

          if (newBracket[nextRoundIdx] && newBracket[nextRoundIdx][nextMatchIdx]) {
            if (isTeam1) {
              newBracket[nextRoundIdx][nextMatchIdx].team1 = winner;
            } else {
              newBracket[nextRoundIdx][nextMatchIdx].team2 = winner;
            }
            changed = true;
          }
        }
      }
    }

    return changed ? newBracket : bracketToProcess;
  }, [brackets]);

  // Calcola l'altezza totale della colonna in base al numero di match
  const getColumnHeight = useCallback((roundIndex, matchesInRound, bracket) => {
    // Per la vista lineare, usa un'altezza che tiene conto di tutti i match
    if (viewMode === 'linear') {
      // Anche se i match non sono in prima colonna, garantisci lo spazio per tutti
      return matchesInRound * 120;
    }

    // Per la vista piramidale, calcola l'altezza in base al primo round
    const firstRoundMatches = bracket[0]?.length || 0;
    if (firstRoundMatches === 0) return 200; // Valore di default

    // Calcola l'altezza totale basata sul primo round 
    // Questa garantisce che ci sia abbastanza spazio per tutti i match
    return (firstRoundMatches - 1) * 120 + 100;
  }, [viewMode]);

  // Approccio completamente nuovo per il posizionamento dei match
  const getMatchPosition = useCallback((roundIndex, matchIndex, bracket) => {
    if (viewMode === 'linear') {
      // Vista lineare: i match vengono distribuiti verticalmente in tutte le colonne
      // Questo rende visibili anche i match futuri
      return matchIndex * 120;
    }

    // VISTA PIRAMIDALE - approccio a divisione uniforme

    // Determina il numero di match nel primo round e le dimensioni totali
    const firstRoundMatches = bracket[0]?.length || 0;
    if (firstRoundMatches === 0) return 0;

    // Calcola l'altezza totale disponibile
    const totalHeight = (firstRoundMatches - 1) * 120 + 80;

    // Calcola quanti match ci sono in questo round
    const matchesInRound = bracket[roundIndex]?.length || 0;
    if (matchesInRound <= 1) return totalHeight / 2 - 60; // Match centrale se è solo uno

    // Dividi l'altezza totale in segmenti uniformi per questo round
    const segmentHeight = totalHeight / matchesInRound;

    // Calcola la posizione in modo che sia centrata nel suo segmento
    // Sottrai l'altezza del match/2 per centrarlo esattamente
    return matchIndex * segmentHeight + (segmentHeight / 2) - 60;
  }, [viewMode]);

  // --- VALIDAZIONE BRACKET ---
  const hasValidBracket = useMemo(() => Array.isArray(brackets) && brackets.some(round => Array.isArray(round) && round.some(match => match.team1 && match.team2)), [brackets]);
  const hasValidBracketFromStorage = useMemo(() => {
    const b = loadBracket();
    return Array.isArray(b) && b.some(round => Array.isArray(round) && round.some(match => match.team1 && match.team2));
  }, []);

  // --- RICARICAMENTO BRACKET QUANDO SI TORNA DALLA PAGINA SCOREKEEPER ---
  const reloadBracketFromStorage = useCallback(() => {
    console.log('[EliminationBracket] Ricaricamento bracket da localStorage...');
    const freshBracket = loadBracket();
    if (Array.isArray(freshBracket) && freshBracket.length > 0) {
      setBrackets(freshBracket);
      console.log('[EliminationBracket] Bracket ricaricato:', freshBracket);
    }
  }, []);

  useEffect(() => {

    // Ricarica quando la pagina diventa visibile (tornando da ScoreKeeper)
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        reloadBracketFromStorage();
      }
    };

    // Ricarica quando viene dispatchato l'evento matchesUpdated
    const handleMatchesUpdated = () => {
      reloadBracketFromStorage();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('matchesUpdated', handleMatchesUpdated);
    window.addEventListener('focus', reloadBracketFromStorage);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('matchesUpdated', handleMatchesUpdated);
      window.removeEventListener('focus', reloadBracketFromStorage);
    };
  }, [reloadBracketFromStorage]);

  useEffect(() => {
    // Log per debug del bracket e dei team
    console.log("EliminationBracket - Dati iniziali:", {
      route: route?.params,
      storageTeams: loadTeams()?.length,
      storageMatches: loadMatches()?.length,
      storageBracket: loadBracket()
    });

    // Controllo se ci sono atleti qualificati salvati da RoundRobinTable
    const qualifiedAthletes = JSON.parse(localStorage.getItem('qualified_athletes') || '[]');
    const competitionType = localStorage.getItem('competitionType') || 'team';
    console.log("Atleti qualificati trovati in localStorage:", qualifiedAthletes.length);

    let teams = [];
    let standings = [];

    // Verifica se c'è un bracket passato tramite navigate/state
    if (route && route.params) {
      if (route.params.teams) teams = route.params.teams;
      if (route.params.standings) standings = route.params.standings;
      if (route.params.bracket) {
        const bracketFromParams = route.params.bracket;
        setIncomingBracket(bracketFromParams);
        console.log("Bracket ricevuto direttamente tramite navigate/state", bracketFromParams.length, "rounds");
      }
    }

    // Se non ci sono team dai parametri, carica da localStorage
    if (!teams.length) {
      try { teams = loadTeams() || []; } catch (e) { teams = []; }
    }
    // AUTODETECT tipo gara: se tutti i teams hanno più di 1 membro, forzo 'team'
    if (
      Array.isArray(teams) &&
      teams.length > 0 &&
      teams.every(t => Array.isArray(t.members) && t.members.length > 1)
    ) {
      if (competitionType !== 'mixed') {
        localStorage.setItem('competitionType', 'team');
        console.log('[EliminationBracket] Competizione rilevata: TEAM, imposto competitionType=team');
      }
    } else if (
      Array.isArray(teams) &&
      teams.length > 0 &&
      teams.every(t => Array.isArray(t.members) && t.members.length === 1)
    ) {
      localStorage.setItem('competitionType', 'individual');
      console.log('[EliminationBracket] Competizione rilevata: INDIVIDUAL, imposto competitionType=individual');
    }

    // Calcola standings come in RoundRobinTable se non presenti
    if (!standings.length && teams.length) {
      const matches = loadMatches() || [];
      const validMatches = matches.filter(match => {
        const teamA = teams[match.teamA];
        const teamB = teams[match.teamB];
        if (!teamA || !teamB) return false;
        if (!Array.isArray(teamA.members) || !Array.isArray(teamB.members)) return false;
        if (teamA.members.length === 0 || teamB.members.length === 0) return false;
        return true;
      });
      const teamStats = teams.map((team, index) => ({
        id: index,
        name: team.name,
        wins: 0,
        losses: 0,
        pointsScored: 0,
        pointsConceded: 0,
        matchesPlayed: 0
      }));
      validMatches.forEach(match => {
        if (match.completed) {
          const teamAIndex = match.teamA;
          const teamBIndex = match.teamB;
          const scoreA = match.scoreA || 0;
          const scoreB = match.scoreB || 0;
          if (scoreA > scoreB) {
            teamStats[teamAIndex].wins++;
            teamStats[teamBIndex].losses++;
          } else if (scoreB > scoreA) {
            teamStats[teamBIndex].wins++;
            teamStats[teamAIndex].losses++;
          }
          teamStats[teamAIndex].pointsScored += scoreA;
          teamStats[teamAIndex].pointsConceded += scoreB;
          teamStats[teamBIndex].pointsScored += scoreB;
          teamStats[teamBIndex].pointsConceded += scoreA;
          teamStats[teamAIndex].matchesPlayed++;
          teamStats[teamBIndex].matchesPlayed++;
        }
      });
      standings = [...teamStats].sort((a, b) => {
        if (b.wins !== a.wins) return b.wins - a.wins;
        if (b.pointsScored !== a.pointsScored) return b.pointsScored - a.pointsScored;
        return a.pointsConceded - b.pointsConceded;
      });
    }
    setLocalTeams(teams);
    setLocalStandings(standings);

    // Gestisco anche il caso in cui arrivi già un bracket nei parametri route
    if (route?.params?.bracket) {
      const bracketFromParams = route.params.bracket;
      console.log("Bracket ricevuto tramite navigate/state", bracketFromParams.length, "rounds");

      // IMPORTANTE: applica sempre l'avanzamento dei bye
      const updatedBracket = advanceByeWinners(bracketFromParams);
      setBrackets(updatedBracket);

      // Salva il bracket aggiornato in localStorage
      saveBracket(updatedBracket);
      console.log("Bracket dai parametri processato con bye e salvato in localStorage");
    }
  }, [route, advanceByeWinners]);

  // Funzione di reset locale rimossa (inutilizzata)

  // Controllo bracket corrotto (più permissivo)
  const checkCorrupted = (bracketArr) => {
    if (!Array.isArray(bracketArr) || !bracketArr.length) return true;
    let validRounds = 0;
    for (let round of bracketArr) {
      if (!Array.isArray(round)) continue;
      let validMatches = 0;
      for (let match of round) {
        if (!match.team1 || !match.team2) continue;
        if ((typeof match.team1 === 'object' && match.team1.name) || typeof match.team1 === 'string')
          if ((typeof match.team2 === 'object' && match.team2.name) || typeof match.team2 === 'string')
            validMatches++;
      }
      if (validMatches > 0) validRounds++;
    }
    return validRounds === 0;
  };

  // Verifica se i dati sono corrotti, ma usa solo brackets.length come dipendenza
  // per evitare cicli infiniti con altri useEffect che modificano brackets
  useEffect(() => {
    if (brackets && Array.isArray(brackets)) {
      const isCorrupted = checkCorrupted(brackets);
      setDataCorrupted(isCorrupted);
    }
  }, [brackets]); // Segue i cambiamenti del bracket

  // Nuovo sistema di avanzamento by forzato
  useEffect(() => {
    // Controlla se stiamo arrivando da un pulsante che richiede l'avanzamento dei bye
    const byeAdvancementNeeded = localStorage.getItem('byeAdvancementNeeded') === 'true';

    if (byeAdvancementNeeded && Array.isArray(brackets) && brackets.length > 0) {
      console.log("Avanzamento bye forzato richiesto!");

      // Applica l'avanzamento dei bye
      const updatedBracket = advanceByeWinners(brackets);

      // Salva in localStorage
      saveBracket(updatedBracket);

      // Rimuovi il flag
      localStorage.removeItem('byeAdvancementNeeded');

      // Aggiorna lo stato (importante: deve essere fatto prima del reload)
      setBrackets(updatedBracket);

      // Forza un reload SOLO se effettivamente ci sono bye da avanzare
      // (confronta bracket originale con quello dopo advanceByeWinners)
      let byeFound = false;
      for (let r = 0; r < brackets.length && !byeFound; r++) {
        for (let m = 0; m < brackets[r].length && !byeFound; m++) {
          const match = brackets[r][m];
          if ((match.team1?.name === 'BYE' && match.team2?.name !== 'BYE' && match.team2?.name !== 'TBD') ||
            (match.team2?.name === 'BYE' && match.team1?.name !== 'BYE' && match.team1?.name !== 'TBD')) {
            byeFound = true;
          }
        }
      }

      if (byeFound) {
        console.log("Trovati bye da avanzare, forzo refresh della pagina tra 100ms");
        setTimeout(() => {
          window.location.reload();
        }, 100); // Breve timeout per permettere il commit dello stato
      }
    }
  }, [brackets, advanceByeWinners]);

  // Auto-fix per bracket non validi (es. A vs Bye con 2 squadre)
  // ORA CHE IL GENERATORE È FIXATO (bracketSize=2), POSSIAMO RIABILITARE QUESTO FIX SENZA RISCHIO LOOP
  useEffect(() => {
    if (localTeams.length >= 2 && brackets.length === 1 && brackets[0].length === 1) {
      const match = brackets[0][0];
      const hasBye = match.team1?.name === 'BYE' || match.team2?.name === 'BYE';
      // Se abbiamo almeno 2 squadre ma la finale è contro un BYE, è un errore di generazione (fatto prima del fix)
      if (hasBye) {
        console.log("Rilevato bracket invalido (A vs BYE con 2 squadre). Rigenerazione forzata.");
        localStorage.removeItem('bracket');
        window.location.reload();
      }
    }
  }, [brackets, localTeams.length]);

  useEffect(() => {
    if (!brackets.length && localTeams.length && localStandings.length) {
      console.log("Generazione bracket dal EliminationBracket.js con:", {
        localTeams: localTeams.length,
        localStandings: localStandings.length,
        localTeamsArr: localTeams,
        localStandingsArr: localStandings,
        incomingBracketPresent: incomingBracket !== null
      });

      // Controlla prima se c'è un bracket ricevuto direttamente dal navigate/state
      if (incomingBracket !== null && Array.isArray(incomingBracket) && incomingBracket.length > 0) {
        console.log("Usando bracket ricevuto direttamente dai parametri di navigazione");
        // PATCH: aggiorna sempre i nomi delle squadre!
        const patchedBracket = patchBracketTeamNames(incomingBracket, localTeams);

        // Applica subito l'avanzamento bye e salva il bracket aggiornato
        const updatedBracket = advanceByeWinners(patchedBracket);
        setBrackets(updatedBracket);
        saveBracket(updatedBracket);
        console.log("Bracket dai parametri processato con bye e salvato in localStorage");

        // Forziamo refresh dei dati per sicurezza
        setTimeout(() => {
          const freshBracket = advanceByeWinners(updatedBracket);
          setBrackets([...freshBracket]);
          saveBracket(freshBracket);
          console.log("Refresh forzato del bracket completato");
        }, 100);
        return;
      }

      // Se non c'è un bracket dai parametri, controlla in localStorage
      const existingBracket = loadBracket();
      if (Array.isArray(existingBracket) && existingBracket.length > 0) {
        console.log("Usando bracket esistente da localStorage", existingBracket);
        // PATCH: aggiorna sempre i nomi delle squadre!
        const patchedBracket = patchBracketTeamNames(existingBracket, localTeams);

        // Applica l'avanzamento automatico dei bye
        console.log("Applico avanzamento automatico bye dopo caricamento da localStorage");
        const updatedBracket = advanceByeWinners(patchedBracket);

        // Processa e salva il bracket aggiornato
        setBrackets(updatedBracket);
        saveBracket(updatedBracket);
        console.log("Bracket da localStorage processato con bye e salvato");
        return;
      }

      // Inline: genera brackets senza dipendenza esterna
      let rounds = [];

      const numTeams = localTeams.length;
      const numRounds = Math.ceil(Math.log2(numTeams));
      const totalTeams = Math.pow(2, numRounds);

      // Log per debug
      console.log(`Creazione bracket con ${numTeams} atleti, ${numRounds} round, ${totalTeams} posizioni totali`);

      for (let i = 0; i < numRounds; i++) {
        const roundMatches = [];
        const matchesInRound = Math.pow(2, numRounds - i - 1);

        if (i === 0) {
          // NUOVA IMPLEMENTAZIONE: Standard FIE per tabelloni di eliminazione diretta
          // Ordiniamo gli standings per classifica
          const sortedStandings = [...localStandings].sort((a, b) => {
            // Prima per numero di vittorie decrescente
            if (b.wins !== a.wins) return b.wins - a.wins;
            // Poi per differenza stoccate decrescente
            const diffA = a.pointsScored - a.pointsConceded;
            const diffB = b.pointsScored - b.pointsConceded;
            if (diffB !== diffA) return diffB - diffA;
            // Infine per stoccate fatte
            return b.pointsScored - a.pointsScored;
          });

          console.log('Classificati ordinati:', sortedStandings.map(s => s?.name));

          // Calcolo delle posizioni nel tabellone secondo lo standard FIE
          const positions = createStandardFIEBracketPositions(matchesInRound, sortedStandings.length);
          console.log('Posizioni nel tabellone:', positions);

          // Creazione degli incontri del primo turno
          for (let j = 0; j < matchesInRound; j++) {
            // Ottieni i due avversari per questa posizione nel tabellone
            const position1 = positions[j * 2];
            const position2 = positions[j * 2 + 1];

            // Determina se sono atleti reali o bye
            const s1 = position1 >= 0 && position1 < sortedStandings.length ? sortedStandings[position1] : null;
            const s2 = position2 >= 0 && position2 < sortedStandings.length ? sortedStandings[position2] : null;

            console.log(`Match ${j + 1}: Posizione ${position1 + 1} vs ${position2 + 1} - ${s1?.name || 'BYE'} vs ${s2?.name || 'BYE'}`);

            // Crea il match con i dati corretti
            const team1 = s1 && typeof s1.id === 'number' && localTeams[s1.id] ? { ...localTeams[s1.id] } : { name: 'BYE' };
            const team2 = s2 && typeof s2.id === 'number' && localTeams[s2.id] ? { ...localTeams[s2.id] } : { name: 'BYE' };
            team1.name = getTeamDisplayName(team1);
            team2.name = getTeamDisplayName(team2);

            console.log(`[GEN] team1:`, team1, `[GEN] team2:`, team2);


            const assalti = generateAssaltiFederali(team1?.members, team2?.members);
            // Recupera il valore di stoccatePerAssalto specifico per l'eliminazione diretta
            const competitionType = localStorage.getItem('competitionType') || 'individual';
            let stoccatePerAssalto = 5;

            if (competitionType === 'individual') {
              const saved = localStorage.getItem('individual_stoccateElimDiretta');
              stoccatePerAssalto = saved ? parseInt(saved) : 15;
            } else if (competitionType === 'team') {
              const saved = localStorage.getItem('team_stoccateElimDiretta');
              stoccatePerAssalto = saved ? parseInt(saved) : 5;
            } else if (competitionType === 'mixed') {
              const saved = localStorage.getItem('mixed_stoccateElimDiretta');
              stoccatePerAssalto = saved ? parseInt(saved) : 12;
            } else {
              // Fallback generico se mancano i dati specifici
              const saved = localStorage.getItem('stoccatePerAssalto');
              if (saved) stoccatePerAssalto = parseInt(saved);
            }
            // Se uno dei team è BYE, il match è già completato e il vincitore è l'altro team
            const matchData = {
              team1: { ...team1 },
              team2: { ...team2 },
              winner: null,
              score1: team2.name === 'BYE' ? stoccatePerAssalto : 0,
              score2: team1.name === 'BYE' ? stoccatePerAssalto : 0,
              completed: team1.name === 'BYE' || team2.name === 'BYE', // Auto-completato se c'è un BYE
              assalti,
              numeroAssalti: assalti.length,
              stoccatePerAssalto,
              stoccateTotali: assalti.length * stoccatePerAssalto,
            };

            // Imposta il vincitore automaticamente se è un match con BYE
            if (team1.name === 'BYE') {
              matchData.winner = 2; // team2
            } else if (team2.name === 'BYE') {
              matchData.winner = 1; // team1
            }

            roundMatches.push(matchData);
          }
        } else {
          // Per i round successivi al primo, creiamo match vuoti
          for (let j = 0; j < matchesInRound; j++) {
            roundMatches.push({
              team1: { name: "TBD" },
              team2: { name: "TBD" },
              winner: null,
              score1: 0,
              score2: 0,
              completed: false,
            });
          }
        }
        rounds.push(roundMatches);
      }
      setBrackets(rounds);
      saveBracket(rounds);
      console.log("Bracket generato e salvato con", rounds.length, "rounds");
    } else if (!brackets.length && loadBracket()) {
      const stored = loadBracket();
      console.log("Ripristino bracket da localStorage:", stored.length, "rounds");
      setBrackets(stored);
    }
  }, [localTeams, localStandings, route, setBrackets, getTeamDisplayName, incomingBracket, advanceByeWinners, patchBracketTeamNames, brackets.length]); // Rimosso brackets dalle dipendenze per evitare ciclo infinito

  // Applica avanzamento BYE dopo generazione bracket o aggiornamento punteggio
  // Usiamo una ref per tenere traccia se abbiamo già processato questi brackets
  useEffect(() => {
    // Applichiamo l'avanzamento dei bye ad ogni cambio del bracket per reattività
    if (brackets.length > 0) {
      const advanced = advanceByeWinners();

      // Verifichiamo che ci sia effettivamente stato un cambiamento reale nel contenuto prima di aggiornare lo stato
      // Questo previene loop infiniti causati da nuove referenze di oggetti identici
      const hasChanged = JSON.stringify(advanced) !== JSON.stringify(brackets);

      if (hasChanged) {
        setBrackets(advanced);
        saveBracket(advanced);
      }
    }
  }, [brackets, advanceByeWinners]);


  // --- FINE LOGICA AVANZAMENTO BYE ---

  // --- LOGICA AVANZAMENTO VINCITORI ---
  useEffect(() => {
    if (brackets.length <= 1) return;

    // Clona il bracket per le modifiche
    const updatedBracket = JSON.parse(JSON.stringify(brackets));
    let needsUpdate = false;

    // Scorri tutti i round tranne l'ultimo
    for (let r = 0; r < updatedBracket.length - 1; r++) {
      for (let m = 0; m < updatedBracket[r].length; m++) {
        const match = updatedBracket[r][m];

        // Se il match è completato e ha un vincitore
        if (match.completed && match.winner) {
          // Calcola in quale match del round successivo andrà il vincitore
          const nextRoundIdx = r + 1;
          const nextMatchIdx = Math.floor(m / 2);

          // Verifica se il round successivo e il match esistono
          if (updatedBracket[nextRoundIdx] && updatedBracket[nextRoundIdx][nextMatchIdx]) {
            const nextMatch = updatedBracket[nextRoundIdx][nextMatchIdx];

            // Determina se il vincitore va in posizione team1 o team2
            const winnerPosition = m % 2 === 0 ? 'team1' : 'team2';
            const winnerTeam = match.winner === 1 ? match.team1 : match.team2;

            // Aggiorna la posizione solo se è diversa
            if (JSON.stringify(nextMatch[winnerPosition]) !== JSON.stringify(winnerTeam)) {
              nextMatch[winnerPosition] = winnerTeam;
              needsUpdate = true;
            }
          }
        }
      }
    }

    // Salva solo se ci sono stati cambiamenti
    if (needsUpdate) {
      setBrackets(updatedBracket);
      saveBracket(updatedBracket);
    }
  }, [brackets, setBrackets]);

  // --- FINE LOGICA AVANZAMENTO VINCITORI ---

  // --- INTEGRAZIONE COMPATIBILITÀ BRACKET INDIVIDUALI ---
  const convertIndividualBracketToRounds = useCallback((bracket) => {
    // bracket: { players: [...], matches: [...] }
    if (!bracket || !Array.isArray(bracket.players) || !Array.isArray(bracket.matches)) return [];
    // Raggruppa tutti i match come primo round, i vincitori andranno nei round successivi
    const firstRound = bracket.matches.map(m => ({
      team1: { ...m.playerA, name: m.playerA?.name + (typeof m.playerA?.pool !== 'undefined' ? ` (G${m.playerA.pool + 1})` : '') },
      team2: { ...m.playerB, name: m.playerB?.name + (typeof m.playerB?.pool !== 'undefined' ? ` (G${m.playerB.pool + 1})` : '') },
      winner: m.winner || null,
      score1: m.scoreA || 0,
      score2: m.scoreB || 0,
      completed: m.completed || false,
      stoccate: m.stoccate,
      assalti: m.assalti || [],
      stoccateTotali: m.stoccate || 0
    }));
    // Genera round successivi vuoti (TBD)
    let rounds = [firstRound];
    let n = firstRound.length;
    while (n > 1) {
      n = Math.floor(n / 2);
      const next = [];
      for (let i = 0; i < n; i++) {
        next.push({
          team1: { name: "TBD" },
          team2: { name: "TBD" },
          winner: null,
          score1: 0,
          score2: 0,
          completed: false,
        });
      }
      rounds.push(next);
    }
    return rounds;
  }, []);

  // --- FINE INTEGRAZIONE ---

  // Sostituisci la logica per bracketToShow:
  const bracketObj = useMemo(() => {
    try {
      const b = JSON.parse(localStorage.getItem('bracket'));
      if (b && b.players && b.matches) return b;
    } catch { }
    return null;
  }, []);

  const bracketToShow = useMemo(() => {
    if (bracketObj && bracketObj.players && bracketObj.matches) {
      // Bracket individuale: patcha i nomi usando players e teams
      const rounds = convertIndividualBracketToRounds(bracketObj);
      return patchBracketTeamNames(rounds, localTeams.length ? localTeams : bracketObj.players);
    } else if (hasValidBracket) {
      console.log('[bracketToShow] patchBracketTeamNames su brackets:', brackets, localTeams);
      return patchBracketTeamNames(brackets, localTeams);
    } else if (hasValidBracketFromStorage) {
      console.log('[bracketToShow] patchBracketTeamNames su loadBracket:', loadBracket(), localTeams);
      return patchBracketTeamNames(loadBracket(), localTeams);
    } else {
      return [];
    }
  }, [bracketObj, hasValidBracket, hasValidBracketFromStorage, brackets, localTeams, convertIndividualBracketToRounds, patchBracketTeamNames]);



  // Funzione di click sul match: solo se entrambe le squadre sono reali
  const handleMatchClick = useCallback((roundIndex, matchIndex, match) => {
    if (
      match.team1?.name !== 'BYE' && match.team2?.name !== 'BYE' &&
      match.team1?.name !== 'TBD' && match.team2?.name !== 'TBD'
    ) {
      navigate('/score', {
        state: {
          roundIdx: roundIndex,
          matchIdx: matchIndex,
          bracketMode: true,
          from: 'bracket'
        }
      });
    }
  }, [navigate]);

  // Gestione click su punteggio: apre ScoreKeeper per inserimento dettagli
  const handleScore = useCallback((roundIdx, matchIdx) => {
    const match = bracketToShow[roundIdx][matchIdx];
    if (
      match.team1?.name !== 'BYE' && match.team2?.name !== 'BYE' &&
      match.team1?.name !== 'TBD' && match.team2?.name !== 'TBD'
    ) {
      navigate('/score', {
        state: {
          roundIndex: roundIdx,
          matchIndex: matchIdx,
          bracketMode: true,
          from: 'bracket'
        }
      });
    }
  }, [navigate, bracketToShow]);

  const bracketButtonStyle = {
    ...styles.matchScore,
    backgroundColor: '#1976d2',
    color: '#fff',
    marginLeft: 0,
    marginRight: 6,
    paddingHorizontal: 0,
    minWidth: 26,
    maxWidth: 32,
    elevation: 2,
    fontFamily: 'monospace',
    borderRadius: 10,
    borderColor: '#fff',
    borderWidth: 2,
    shadowColor: '#000',
    shadowOpacity: 0.10,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 1 },
  };

  // Stili pulsanti rimossi (inutilizzati)

  // --- Funzione di reset bracket con modale custom ---
  const confirmResetBracket = useCallback(() => {
    localStorage.removeItem('bracket');
    window.location.reload();
  }, []);

  // Funzione rigenerazione rimossa (inutilizzata)

  if (dataCorrupted || (!hasValidBracket && !hasValidBracketFromStorage)) {
    return (
      <View style={{ padding: 32, alignItems: 'center', justifyContent: 'center' }}>
        <Text style={{ fontSize: 20, fontWeight: 'bold', color: '#1976d2', marginBottom: 20 }}>
          Nessun dato disponibile
        </Text>
        <Text style={{ fontSize: 16, color: '#666', textAlign: 'center', marginBottom: 30 }}>
          Non è stato trovato nessun incontro pianificato per la fase eliminatoria. Verifica di aver completato la fase a gironi.
        </Text>

        <View style={{ flexDirection: 'row', justifyContent: 'center', marginTop: 20 }}>
          <TouchableOpacity
            style={{
              backgroundColor: '#1976d2',
              paddingVertical: 12,
              paddingHorizontal: 24,
              borderRadius: 24,
              elevation: 2,
              shadowColor: '#1976d2',
              shadowOffset: { width: 0, height: 3 },
              shadowOpacity: 0.18,
              shadowRadius: 4
            }}
            onPress={() => navigate('/')}
          >
            <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 17 }}>Crea Nuova Gara</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Se l'accesso è negato, mostriamo una pagina statica
  if (accessDenied) {
    return (
      <View style={{
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        backgroundColor: '#f5f5f5',
        height: '100vh'
      }}>
        <View style={{
          backgroundColor: 'white',
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: 500,
          alignItems: 'center',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 3 },
          shadowOpacity: 0.1,
          shadowRadius: 8,
          elevation: 4
        }}>
          <Text style={{ fontSize: 28, fontWeight: 'bold', color: '#f44336', marginBottom: 12 }}>Accesso non consentito</Text>
          <View style={{ height: 4, backgroundColor: '#f44336', width: 60, marginBottom: 24, borderRadius: 2, opacity: 0.5 }} />

          <Text style={{ fontSize: 18, textAlign: 'center', marginBottom: 12, color: '#333' }}>
            Per accedere al tabellone eliminatorio è necessario completare tutti gli assalti del girone.
          </Text>

          <Text style={{ fontSize: 16, textAlign: 'center', marginBottom: 24, color: '#666' }}>
            Torna alla pagina degli assalti per completare quelli rimanenti.
          </Text>

          <TouchableOpacity
            onPress={handleReturnToMatches}
            className="common-button"
            style={{
              width: '100%',
              paddingVertical: 12,
              paddingHorizontal: 24,
              borderRadius: 24,
              backgroundColor: '#1976d2',
              marginVertical: 16,
              boxShadow: '0 3px 4px rgba(25, 118, 210, 0.18)',
              elevation: 3,
              alignItems: 'center'
            }}
          >
            <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 17 }}>Torna agli assalti</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  // Se l'accesso è consentito, mostriamo il tabellone
  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      {/* Titolo nello stesso stile di MatchSchedule */}
      <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', marginTop: 10 }}>
        <View style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          {/* Icona medaglia oro */}
          <TrophyIcon style={{ fontSize: 44, color: '#1976d2', marginRight: 12 }} />
          {/* Titolo */}
          <Text style={{ fontSize: 32, fontWeight: 'bold', color: '#1976d2', margin: 0, textAlign: 'center', letterSpacing: 1, textShadow: '0px 2px 4px rgba(0,0,0,0.06)' }}>
            Tabellone
          </Text>
        </View>
        {/* Separatore blu */}
        <View style={{ width: 72, height: 4, backgroundColor: '#1976d2', borderRadius: 2, marginTop: 2, marginBottom: 18, opacity: 0.2 }} />
      </View>

      {/* Controlli visualizzazione compatti */}
      <View style={{
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: 12,
        marginBottom: 24,
        padding: 8,
        backgroundColor: '#f0f0f0',
        borderRadius: 12,
        maxWidth: 500,
        alignSelf: 'center'
      }}>
        {/* Selettore vista e zoom integrati */}
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {/* Zoom - */}
          <TouchableOpacity
            style={{
              backgroundColor: '#e0e0e0',
              paddingVertical: 8,
              paddingHorizontal: 12,
              borderRadius: 20,
              marginRight: 8,
            }}
            onPress={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.1))}
          >
            <Text style={{ color: '#555', fontWeight: 'bold', fontSize: 16 }}>-</Text>
          </TouchableOpacity>

          {/* Modalità Piramide */}
          <TouchableOpacity
            style={{
              backgroundColor: viewMode === 'pyramid' ? '#1976d2' : '#e0e0e0',
              paddingVertical: 8,
              paddingHorizontal: 16,
              borderTopLeftRadius: 20,
              borderBottomLeftRadius: 20,
              marginRight: 1,
            }}
            onPress={() => setViewMode('pyramid')}
          >
            <Text style={{ color: viewMode === 'pyramid' ? '#fff' : '#555', fontWeight: 'bold' }}>Piramide</Text>
          </TouchableOpacity>

          {/* Modalità Lineare */}
          <TouchableOpacity
            style={{
              backgroundColor: viewMode === 'linear' ? '#1976d2' : '#e0e0e0',
              paddingVertical: 8,
              paddingHorizontal: 16,
              borderTopRightRadius: 20,
              borderBottomRightRadius: 20,
              marginRight: 8,
            }}
            onPress={() => setViewMode('linear')}
          >
            <Text style={{ color: viewMode === 'linear' ? '#fff' : '#555', fontWeight: 'bold' }}>Lineare</Text>
          </TouchableOpacity>

          {/* Zoom + */}
          <TouchableOpacity
            style={{
              backgroundColor: '#e0e0e0',
              paddingVertical: 8,
              paddingHorizontal: 12,
              borderRadius: 20,
            }}
            onPress={() => setZoomLevel(Math.min(2, zoomLevel + 0.1))}
          >
            <Text style={{ color: '#555', fontWeight: 'bold', fontSize: 16 }}>+</Text>
          </TouchableOpacity>


        </View>
      </View>

      <ScrollView
        horizontal
        contentContainerStyle={{
          paddingHorizontal: 18,
          paddingBottom: 24,
          transform: [{ scale: zoomLevel }],
          transformOrigin: 'top left'
        }}
      >
        <View style={{ width: '100%' }}>
          <View style={styles.bracketContainer}>
            {bracketToShow.map((round, roundIndex) => (
              <View key={roundIndex} style={styles.round}>
                <Text style={[styles.roundTitle, roundIndex === bracketToShow.length - 1 && { backgroundColor: '#ffd700', color: '#222' }]}>
                  {roundIndex === bracketToShow.length - 1 ? "Finale" :
                    roundIndex === bracketToShow.length - 2 ? "Semifinali" :
                      roundIndex === bracketToShow.length - 3 ? "Quarti di finale" :
                        roundIndex === bracketToShow.length - 4 ? "Ottavi di finale" :
                          roundIndex === bracketToShow.length - 5 ? "Sedicesimi di finale" :
                            roundIndex === bracketToShow.length - 6 ? "Trentaduesimi di finale" :
                              `Turno ${roundIndex + 1}`}
                </Text>
                <View style={styles.matchesColumn}>
                  {/* Spacer container per gestire lo spazio verticale quando si usa posizione assoluta */}
                  <View style={{
                    height: getColumnHeight(roundIndex, round.length, bracketToShow),
                    position: 'relative'
                  }}>
                    {round.map((match, matchIndex) => (
                      <TouchableOpacity
                        key={matchIndex}
                        onPress={() => handleMatchClick(roundIndex, matchIndex, match)}
                        disabled={match.team1?.name === 'BYE' || match.team2?.name === 'BYE' || match.team1?.name === 'TBD' || match.team2?.name === 'TBD'}
                        style={{
                          position: 'absolute',
                          top: getMatchPosition(roundIndex, matchIndex, bracketToShow),
                          left: 0,
                          right: 0,
                          zIndex: 1
                        }}
                      >
                        <View style={[
                          styles.matchCard,
                          {
                            shadowColor: '#1976d2',
                            shadowOffset: { width: 0, height: 3 },
                            shadowOpacity: 0.13,
                            shadowRadius: 8,
                            elevation: 3
                          },
                          match.winner === 1 ? styles.winnerHighlight : match.winner === 2 ? styles.winnerHighlight : null,
                          // Stile per match non ancora disputati (più visibili)
                          (!match.completed && match.team1?.name !== 'TBD' && match.team2?.name !== 'TBD' &&
                            match.team1?.name !== 'BYE' && match.team2?.name !== 'BYE') && {
                            backgroundColor: '#f9f9f9',
                            borderWidth: 1,
                            borderColor: '#1976d2',
                            borderStyle: 'dashed',
                          }
                        ]}
                        >
                          <View style={[
                            styles.teamRow,
                            match.winner === 1 && styles.winnerRow,
                            match.winner === 1 && { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 },
                            roundIndex === 0 && { borderLeftWidth: 3, borderLeftColor: '#1976d2' }
                          ]}>
                            {/* Posizione in classifica (solo primo round) */}
                            {roundIndex === 0 && (
                              <View style={styles.seedingPosition}>
                                <Text style={styles.seedingNumber}>
                                  {getTeamSeedingPosition(match.team1) || '-'}
                                </Text>
                              </View>
                            )}
                            <Text style={[styles.teamName, match.winner === 1 && styles.winnerName]}>
                              {getTeamDisplayName(match.team1)}
                            </Text>
                            <TouchableOpacity onPress={e => handleScore(roundIndex, matchIndex, 1, e)} style={bracketButtonStyle}>
                              <Text style={{ color: '#fff', fontFamily: 'monospace', fontWeight: '900', fontSize: 13, letterSpacing: 1, textAlign: 'center', paddingVertical: 2 }}>{match.score1.toString().padStart(2, '0')}</Text>
                            </TouchableOpacity>
                          </View>
                          <View style={[
                            styles.teamRow,
                            match.winner === 2 && styles.winnerRow,
                            match.winner === 2 && { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
                            roundIndex === 0 && { borderLeftWidth: 3, borderLeftColor: '#1976d2' }
                          ]}>
                            {/* Posizione in classifica (solo primo round) */}
                            {roundIndex === 0 && (
                              <View style={styles.seedingPosition}>
                                <Text style={styles.seedingNumber}>
                                  {getTeamSeedingPosition(match.team2) || '-'}
                                </Text>
                              </View>
                            )}
                            <Text style={[styles.teamName, match.winner === 2 && styles.winnerName]}>
                              {getTeamDisplayName(match.team2)}
                            </Text>
                            <TouchableOpacity onPress={e => handleScore(roundIndex, matchIndex, 2, e)} style={bracketButtonStyle}>
                              <Text style={{ color: '#fff', fontFamily: 'monospace', fontWeight: '900', fontSize: 13, letterSpacing: 1, textAlign: 'center', paddingVertical: 2 }}>{match.score2.toString().padStart(2, '0')}</Text>
                            </TouchableOpacity>
                          </View>
                        </View>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              </View>
            ))}
          </View>
        </View>
      </ScrollView>
      {/* Icona rotazione animata */}
      <RotateIcon />
      {/* Pulsante Classifica Finale a larghezza piena */}
      <View style={{ alignItems: 'center', marginTop: 32, marginBottom: 12, width: '100%', paddingHorizontal: 16 }}>
        <TouchableOpacity
          onPress={() => navigate('/final-ranking')}
          style={{
            backgroundColor: '#1976d2',
            paddingVertical: 14,
            paddingHorizontal: 24,
            borderRadius: 24,
            marginVertical: 16,
            width: '100%',
            alignItems: 'center',
            elevation: 2,
            shadowColor: '#1976d2',
            shadowOffset: { width: 0, height: 3 },
            shadowOpacity: 0.18,
            shadowRadius: 4
          }}
        >
          <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 17 }}>Classifica Finale</Text>
        </TouchableOpacity>
      </View>
      {/* MODALE DI CONFERMA RESET (UX moderna, come in index) */}
      {showResetModal && (
        <View style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
          <View style={{
            backgroundColor: '#fff',
            borderRadius: 12,
            padding: 24,
            maxWidth: 400,
            width: '90%',
          }}>
            <Text style={{ fontSize: 20, fontWeight: 'bold', marginBottom: 12 }}>
              Conferma reset
            </Text>
            <Text style={{ fontSize: 16, color: '#666', marginBottom: 24 }}>
              ⚠️ Stai per resettare il tabellone eliminazione. Tutti i risultati di questa fase andranno persi. Vuoi procedere?
            </Text>
            <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 20 }}>
              <TouchableOpacity
                onPress={() => setShowResetModal(false)}
                style={{
                  backgroundColor: '#f0f0f0',
                  color: '#333',
                  border: '1px solid #ddd',
                  paddingVertical: 10,
                  paddingHorizontal: 20,
                  borderRadius: 8,
                  minWidth: 120,
                  alignItems: 'center',
                  transition: 'all 0.2s ease'
                }}
              >
                <Text style={{ color: '#333', fontWeight: '500', fontSize: 16 }}>Annulla</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={confirmResetBracket}
                className="common-button danger"
                style={{
                  alignItems: 'center',
                  minWidth: 120,
                }}
              >
                <Text style={{ color: '#fff', fontWeight: '500', fontSize: 16 }}>Sì, resetta</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },
  titleShadow: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#1976d2',
    textAlign: 'center',
    marginBottom: 2,
    textShadow: '0px 2px 4px rgba(0, 0, 0, 0.06)',
  },
  titleUnderline: {
    height: 4,
    backgroundColor: '#1976d2',
    width: 72,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 18,
    opacity: 0.2,
  },
  bracketContainer: {
    flexDirection: 'row',
    paddingBottom: 20,
  },
  round: {
    marginRight: 32, // Aumentato da 24 a 32 per maggiore distanza tra colonne
    width: 220, // Aumentato da 200 a 220 per dare più spazio ai nomi
  },
  roundTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 16, // Aumentato da 12 a 16
    textAlign: 'center',
    backgroundColor: '#1976d2',
    color: '#fff',
    paddingVertical: 6,
    borderRadius: 12,
  },
  matchesColumn: {
    flex: 1,
  },
  matchCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 0,
    marginBottom: 26, // ancora più spazio tra match
    minHeight: 46, // più compatto
    boxShadow: '0px 3px 8px rgba(25, 118, 210, 0.13)',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  teamRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6, // più sottile
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#e3e3e3',
    backgroundColor: '#f5f5f5', // Cambiato da blu a grigio chiaro per team normali
    borderRadius: 12, // Usiamo un raggio uniforme e sovrascriviamo dove serve
    marginBottom: 2, // aggiunto per separazione visiva
  },
  winnerRow: {
    backgroundColor: '#1976d2', // Cambiato da verde a blu per evidenziare il vincitore
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
  },
  teamName: {
    flex: 1,
    color: '#555', // Cambiato da bianco a grigio scuro per team normali
    fontWeight: 'bold',
    fontSize: 13, // più piccolo
    textAlign: 'right',
    marginRight: 14, // aumentato spazio
    letterSpacing: 0.2,
    textShadow: '0px 1px 2px rgba(0, 0, 0, 0.13)',
    textTransform: 'uppercase',
  },
  winnerName: {
    color: '#fff', // Testo bianco per il nome del vincitore su sfondo blu
  },
  matchScore: {
    fontWeight: 'bold',
    width: 26, // più compatto
    minWidth: 26,
    maxWidth: 32,
    textAlign: 'center',
    fontSize: 13, // più piccolo
    color: '#555', // Cambiato da blu a grigio scuro
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingVertical: 2,
  },
  winnerHighlight: {
    boxShadow: '0px 6px 16px rgba(25, 118, 210, 0.22)',
    borderWidth: 2,
    borderColor: '#1976d2', // Cambiato da oro a blu
  },
  // Stili per la visualizzazione della posizione nel girone
  seedingPosition: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#1976d2',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
    marginLeft: -5,
  },
  seedingNumber: {
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 12,
    textAlign: 'center',
  },
});

export default EliminationBracket;